import { useState, useEffect } from "react";
import { useFormContext } from "../contexts/FormContext";
import { sessionStorageUtils } from "../../utility/utility";
import {
  SchedulerConfig,
  Field,
  Element,
} from "../../Interface/scheduler/form";
import { useSharedStateWithActions } from "./useSharedState";
import { sharedStateKeys } from "../../constants/constant";
import { isCaPostalCode } from "../components/Helper/Helper";

interface UseHandleRedirectionFromShortFormProps {
  formConfig: SchedulerConfig | null;
  currentStep: number;
  setSelectedNearByLocationData: (
    franchiseId: number,
    franchiseWeblocationId: number,
  ) => void;
}

interface UseHandleRedirectionFromShortFormReturn {
  isShortFormDataSet: boolean;
  setIsShortFormDataSet: (value: boolean) => void;
}

export const useHandleRedirectionFromShortForm = ({
  formConfig,
  currentStep,
  setSelectedNearByLocationData,
}: UseHandleRedirectionFromShortFormProps): UseHandleRedirectionFromShortFormReturn => {
  const { setFormValues } = useFormContext();
  const [isShortFormDataSet, setIsShortFormDataSet] = useState(false);
  const { setValue } = useSharedStateWithActions();

  const shouldRedirectToLeadFlow = sessionStorageUtils.getItem(
    "shouldRedirectToLeadFlow",
  );

  const updateFormConfig = (id: string) => {
    setValue(sharedStateKeys.MODIFIER_FLOW_ID, id);
  };

  const processRadioGroupImageField = (field: Field, customerType: string) => {
    const selectedOption = field.options?.find(
      (option) => option.id?.toLowerCase() === customerType.toLowerCase(),
    );

    if (selectedOption) {
      updateFormConfig(selectedOption.id);
      return {
        servicetype: selectedOption.id,
        userInfoServiceId: selectedOption.serviceid || "",
        userinfoServiceType: selectedOption.id,
        serviceText: selectedOption.value || "",
      };
    }
    return {};
  };

  const processRadioGroupField = (field: Field, customerType: string) => {
    const selectedOption = field.options?.find(
      (option) => option.id?.toLowerCase() === customerType.toLowerCase(),
    );

    if (selectedOption) {
      updateFormConfig(selectedOption.id);
      return {
        servicetype: selectedOption.id,
      };
    }
    return {};
  };

  const processFieldsForServiceType = (
    elements: Element[],
    customerType: string,
  ) => {
    let serviceTypeFormValues = {};

    elements.forEach((element) => {
      if (element.fields) {
        element.fields.forEach((field) => {
          if (field.type === "radiogroupimage" && field.options?.length > 0) {
            serviceTypeFormValues = {
              ...serviceTypeFormValues,
              ...processRadioGroupImageField(field, customerType),
            };
          } else if (
            field.type === "radio-group" &&
            field.options?.length > 0
          ) {
            serviceTypeFormValues = {
              ...serviceTypeFormValues,
              ...processRadioGroupField(field, customerType),
            };
          }
        });
      }
    });

    return serviceTypeFormValues;
  };

  const getServiceReminderOptin = (shortFormData, isCanadaZipCode) => {
    if (isCanadaZipCode) {
      return !shortFormData.emailOptOut;
    }
    return shortFormData.optIn;
  };

  const handleRedirectionFromShortForm = async (shortFormData) => {
    const isCanadaZipCode = isCaPostalCode(
      shortFormData.zipCode || shortFormData.postalCode,
    );

    const serviceRemainderOptin = getServiceReminderOptin(
      shortFormData,
      isCanadaZipCode,
    );

    const formData = {
      fname: shortFormData.firstName || "",
      lname: shortFormData.lastName || "",
      email: shortFormData.email || "",
      city: shortFormData.city || "",
      state: shortFormData.state || "",
      phone: shortFormData.phone || "",
      address: shortFormData.address || "",
      zipcode: shortFormData.zipCode || "",
      customerType: shortFormData.customerType || "",
      franchiseId: shortFormData?.franchiseId ?? null,
      franchiseWebLocationId: shortFormData?.webLocationId || 0,
      ...(serviceRemainderOptin !== undefined && {
        servicereminder: String(serviceRemainderOptin),
      }),
    };
    const currentFormConfig = formConfig?.steps[currentStep];
    let serviceTypeFormValues = {};

    if (currentFormConfig?.elements) {
      serviceTypeFormValues = processFieldsForServiceType(
        currentFormConfig.elements,
        formData.customerType,
      );
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      ...formData,
      ...serviceTypeFormValues,
    }));

    //Redirection from short-form page via nearby location flow
    const isNearByLocation = sessionStorageUtils.getItem("isNearbyRedirect");
    if (isNearByLocation) {
      setSelectedNearByLocationData(
        formData?.franchiseId,
        formData?.franchiseWebLocationId,
      );
    }

    setIsShortFormDataSet(true);
  };

  useEffect(() => {
    const shouldRedirect = shouldRedirectToLeadFlow
      ? JSON.parse(shouldRedirectToLeadFlow)
      : false;
    const sessionData = sessionStorageUtils.getItem("formValues");
    const shortFormData = sessionData ? JSON.parse(sessionData) : {};
    const dataLayer = globalThis.dataLayer;
    if (
      shouldRedirect &&
      shortFormData &&
      Object.keys(shortFormData).length > 0
    ) {
      if (typeof dataLayer?.push === "function") {
        dataLayer.push({
          event: "shortForm_longForm_skip",
          locationDBA: localStorage.getItem("doingBusinessAs"),
          locationID: localStorage.getItem("franchiseWebLocationId"),
          submitType: "short_to_long_auto_skip",
          formType: "short_form_to_long_form",
          longFormScreen1Skipped: true,
          bookingApiTriggered: true,
          leadFlowType: "shortToLong",
          timestamp: new Date().toISOString(),
        });
      }
      handleRedirectionFromShortForm(shortFormData);
    }
  }, [shouldRedirectToLeadFlow]);

  return {
    isShortFormDataSet,
    setIsShortFormDataSet,
  };
};
